/**
 * bridge/alert_controller.js — "alert" bridge component (Stimulus + Strada).
 *
 * Capability-tier bridge component: presents a NATIVE confirm/alert dialog
 * (title, message, confirm/cancel labels) and resolves the user's choice back to
 * the web layer, so destructive actions (delete quote/invoice/job) can be gated
 * behind a real OS dialog instead of the browser's blocking window.confirm().
 *
 * Identifier "bridge--alert" (glob-derived from this subdir); Strada component
 * name "alert" MUST match the iOS class var name
 * (native/ios/Chippie/Bridge/AlertComponent.swift) and the Android-reached method.
 *
 * Why it extends Strada's BridgeComponent: the iOS dialog is reached via
 * this.send("confirm", data, reply => ...), which only works on a connected bridge
 * component inside the iOS Hotwire Native shell. Extending BridgeComponent gives
 * us that send() transport with a reply callback AND the UA-gated connect().
 *
 * Three-way transport — registered onto the window.ChippieBridge facade as a
 * single async confirm() returning a Promise<boolean>:
 *   - ios:     this.send("confirm", data, response => resolve(response.data.confirmed))
 *   - android: window.ChippieNative.confirm(json, callbackId); the native side
 *              resolves by calling window.__chippieBridgeResolve(callbackId, bool).
 *              (Android has no Strada, so we bridge the async reply through a
 *              global callback registry keyed by id — see __chippieBridgeResolve.)
 *   - pwa:     window.confirm(message) (sync), wrapped in Promise.resolve.
 *
 * Callers: `const ok = await window.ChippieBridge.confirm({ title, message,
 * confirmLabel, cancelLabel }); if (ok) { ...destructive... }`.
 *
 * Template wiring (declarative trigger): put the dialog copy + the action to run
 * on confirm on the element. data-action="bridge--alert#confirm" intercepts the
 * click, shows the native dialog, and only follows the element's href / submits
 * its closest form when the user confirms.
 *   <a href="{{ url_for('quotes.delete', id=q.id) }}"
 *      data-controller="bridge--alert"
 *      data-bridge--alert-title-value="Delete quote?"
 *      data-bridge--alert-message-value="This can't be undone."
 *      data-bridge--alert-confirm-label-value="Delete"
 *      data-action="bridge--alert#confirm">Delete</a>
 *
 * The Android resolver registry + standalone (no-element) facade registration live
 * at module load so confirm() works on Android/PWA even when no controller is
 * connected. On iOS the controller's connect() swaps in the live Strada send.
 *
 * Web side adapted from joemasilotti/bridge-components dialog pattern; the
 * async-Promise + Android callback-id bridge are Chippie additions (the contract's
 * Android transport has no Strada reply channel).
 */
import { BridgeComponent } from "@hotwired/hotwire-native-bridge";

// --- Android async-reply bridge --------------------------------------------
// Android's @JavascriptInterface is fire-and-forget; the native dialog resolves
// asynchronously by calling back into JS. We hand native a callback id and keep
// the matching Promise resolver here until it fires. Module-scoped registry +
// monotonic id so ids never collide across calls.
const _pending = new Map();
let _nextId = 0;

if (!window.__chippieBridgeResolve) {
    // Called by the Android shell: ChippieNativeBridge resolves a confirm dialog
    // via webView.evaluateJavascript("window.__chippieBridgeResolve(id, bool)").
    window.__chippieBridgeResolve = (callbackId, confirmed) => {
        const resolve = _pending.get(callbackId);
        if (!resolve) return;
        _pending.delete(callbackId);
        resolve(!!confirmed);
    };
}

function normalise(data) {
    data = data || {};
    return {
        title: data.title || "",
        message: data.message || "",
        confirmLabel: data.confirmLabel || "OK",
        cancelLabel: data.cancelLabel || "Cancel",
    };
}

// Android + PWA impls hold no per-instance state, so register them once at module
// load. The facade is the SSOT for which one runs; iOS is overridden in connect().
function registerStandalone() {
    if (!window.ChippieBridge || !window.ChippieBridge.register) return;
    window.ChippieBridge.register("confirm", {
        // Placeholder until the controller connects inside the iOS shell. A no-op
        // dialog should never silently green-light a destructive action -> false.
        ios: () => Promise.resolve(false),
        android: (data) => androidConfirm(data),
        pwa: (data) => pwaConfirm(data),
    });
}

function androidConfirm(data) {
    const p = normalise(data);
    if (!window.ChippieNative || !window.ChippieNative.confirm) {
        return pwaConfirm(p);
    }
    return new Promise((resolve) => {
        const id = _nextId++;
        _pending.set(id, resolve);
        try {
            window.ChippieNative.confirm(JSON.stringify(p), id);
        } catch (_e) {
            _pending.delete(id);
            resolve(false);
        }
    });
}

function pwaConfirm(data) {
    const p = normalise(data);
    const text = p.title && p.message ? `${p.title}\n\n${p.message}` : p.title || p.message;
    return Promise.resolve(window.confirm(text));
}

if (window.ChippieBridge) {
    registerStandalone();
} else {
    document.addEventListener("DOMContentLoaded", registerStandalone, { once: true });
}

export default class extends BridgeComponent {
    static component = "alert";

    static values = {
        title: String,
        message: String,
        confirmLabel: String,
        cancelLabel: String,
    };

    connect() {
        super.connect();
        // Inside the iOS shell now: wire the real Strada send into the facade,
        // keeping the Android/PWA fallbacks intact.
        if (!window.ChippieBridge || !window.ChippieBridge.register) return;
        window.ChippieBridge.register("confirm", {
            ios: (data) =>
                new Promise((resolve) => {
                    this.send("confirm", normalise(data), (response) => {
                        resolve(!!(response && response.data && response.data.confirmed));
                    });
                }),
            android: (data) => androidConfirm(data),
            pwa: (data) => pwaConfirm(data),
        });
    }

    disconnect() {
        // Leaving the iOS shell context: drop the live-send impl back to the
        // standalone placeholder so a stale this.send is never invoked.
        registerStandalone();
        super.disconnect();
    }

    // data-action="bridge--alert#confirm" trigger: show the native dialog and only
    // follow the link / submit the form when the user confirms.
    async confirm(event) {
        if (event) event.preventDefault();
        const ok = await window.ChippieBridge.confirm({
            title: this.titleValue,
            message: this.messageValue,
            confirmLabel: this.confirmLabelValue,
            cancelLabel: this.cancelLabelValue,
        });
        if (!ok) return;

        const el = this.element;
        const form = el.closest("form");
        if (el.tagName === "A" && el.href) {
            window.location.href = el.href;
        } else if (form) {
            form.requestSubmit ? form.requestSubmit() : form.submit();
        }
    }
}
